import * as vp from 'vopay';
import moment from 'moment';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import useFetchPortalVideos from '../../../../hooks/useApi/useFetchPortalVideos';
import VideoSearchForm from './VideoSearchForm';
import VideoSearchResult from './VideoSearchResult';
import styles from './VideoContainer.module.scss';
import { useTranslation } from 'react-i18next';

type FC = FunctionComponent;

const { Box, Title } = vp;

const videoTitleOrder = [
  'Introduction',
  'Sending a Single Payment',
  'Request a Single Collection',
  'Scheduled Transactions',
  'Bulk Payment',
  'Split Payments',
  'VoPay Verify',
  'Transaction Approvals',
  'Accounting Software Integrations',
  'One-Time and Recurring Reports',
  'Application and Agreement Guide',
  'ID Verification',
  'Submit a Ticket',
];

const titlesToRemove = [
  'Referral Partner Account',
  'VAR Partner Account',
  'VAR Payfac Partner Account',
  'Creating VoPay Accounts',
  'Partner Account Reports',
];

const VideoContainer: FC = () => {
  const [activeTab, setActiveTab] = useState('Introduction');
  const [isDropdownSelected, setIsDropdownSelected] = useState(false);
  const [hasSearchResult, setHasSearchResult] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredByTabLists, setFilteredByTabLists] = useState<vp.VideoObject[]>([]);
  const [filteredByKeyWordLists, setFilteredByKeyWordLists] = useState<{ [key: string]: vp.VideoObject }>({});
  const [selectedVideo, setSelectedVideo] = useState<vp.VideoObject>({
    title: '',
    url: '',
    thumbnail: '',
    category: '',
    length: 0,
    lastModified: '',
  });
  const [videos, fetchPortalVideos] = useFetchPortalVideos({});
  const [filteredVideoForSearch, setFilteredVideoForSearch] = useState({});
  const { t } = useTranslation('translations', { keyPrefix: 'LearningCentre.VideoTutorials' });

  useEffect(() => {
    fetchPortalVideos({
      IsPartner: false,
    });
  }, []);

  useEffect(() => {
    if (Object.values(videos.VideoList || {}).length >= 1) {
      const filtered = Object.fromEntries(
        Object.entries(videos.VideoList).filter(([, value]) => !titlesToRemove.includes(value.title)),
      );
      setFilteredVideoForSearch(filtered);
      setActiveTab(videos.VideoList[0]?.category || '');
    }
  }, [videos]);

  useEffect(() => {
    const filtered = Object.values(videos.VideoList || {}).filter((video) =>
      video.category?.toLowerCase().includes(activeTab.toLowerCase()),
    );

    const sortedFilteredByTitles = Object.values(videos.VideoList)
      .filter((video) => {
        return filtered.some((value) => value.title === video.title) && !titlesToRemove.includes(video.title);
      })
      .sort((a, b) => {
        const indexA = videoTitleOrder.indexOf(a.title);
        const indexB = videoTitleOrder.indexOf(b.title);

        const adjustedIndexA = indexA === -1 ? videoTitleOrder.length : indexA;
        const adjustedIndexB = indexB === -1 ? videoTitleOrder.length : indexB;

        return adjustedIndexA - adjustedIndexB;
      });
    setFilteredByTabLists(sortedFilteredByTitles);
  }, [activeTab, selectedVideo, videos]);

  useEffect(() => {
    if (!isDropdownSelected) {
      const introVideo = Object.values(videos.VideoList || {}).filter((video) =>
        video.title?.includes('Introduction'),
      )[0];
      if (introVideo) setSelectedVideo(introVideo);
    }
  }, [activeTab, videos]);

  useEffect(() => {
    setHasSearchResult(false);
  }, [selectedVideo]);

  useEffect(() => {
    if (searchTerm === '') setHasSearchResult(false);
  }, [searchTerm]);

  const getSelectedVideo = (video: vp.VideoObject) => {
    setActiveTab(video.category || '');
    setSelectedVideo(video);
    setIsDropdownSelected(true);
    setHasSearchResult(false);
    setSearchTerm('');
  };

  const VideoPlayer = ({ video }: { [key: string]: vp.VideoObject }) => (
    <>
      <h4>{video?.title}</h4>
      <div className={styles.videoPlayer}>
        <video controls width="100%" height="100%" muted autoPlay={false} poster="" controlsList="nodownload">
          <source src={video?.url} type="video/mp4" />
          {t('unsupportedBrowser')}
        </video>
      </div>
    </>
  );

  const MemoizedVideoPlayer = useMemo(() => <VideoPlayer video={selectedVideo} />, [selectedVideo]);

  return (
    <Box flx="col">
      <Box mb="md" jc="sb">
        <Title title={t('title')} />
      </Box>
      <Box flx="row" jc="sb" ai="center" mt="md" gap="md">
        <Box lineHeight="25px">{t('subTitle')}</Box>
        <VideoSearchForm
          videos={filteredVideoForSearch}
          filteredByKeyWordLists={filteredByKeyWordLists}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          setFilteredByKeyWordLists={setFilteredByKeyWordLists}
          setHasSearchResult={setHasSearchResult}
          getSelectedVideo={getSelectedVideo}
        />
      </Box>

      {Object.values(videos.VideoList || {}).length >= 1 && (
        <div className={styles.guideContainer}>
          <div className={styles.guideListsContainer}>
            <div className={styles.guideScrollableList}>
              {filteredByTabLists.map((video, index) => (
                <div
                  key={index}
                  className={[styles.guideList, styles[getActiveClass(video.title)]].join(' ')}
                  onClick={() => {
                    setSelectedVideo(video);
                  }}
                >
                  <div className={styles.thumbnail}>
                    <div className={styles.length}>{String(video.length)?.replace('.', ':')}</div>
                    <img src={video.thumbnail} />
                  </div>
                  <div className={styles.details}>
                    <h5>{video.title}</h5>
                    <span>{moment(video.lastModified).format('MMMM D, YYYY')}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {hasSearchResult ? (
            <VideoSearchResult
              searchTerm={searchTerm}
              filteredByKeyWordLists={filteredByKeyWordLists}
              getSelectedVideo={getSelectedVideo}
            />
          ) : (
            <div className={styles.guide}>{selectedVideo?.title !== '' && MemoizedVideoPlayer}</div>
          )}
        </div>
      )}
    </Box>
  );

  function getActiveClass(title: string) {
    return selectedVideo && title === selectedVideo.title && !hasSearchResult ? 'active' : '';
  }
};

export default VideoContainer;
